import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Badge,
  Checkbox,
  ConfirmationDialog,
  FilterToolbarSearch,
  FormLabel,
  filterToolbarClinicClassName,
  filterToolbarControlLgClassName,
  filterToolbarControlSmClassName,
  filterToolbarSearchGrowClassName,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  Tabs,
  TabsContent,
  TabsCount,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@roger/ui-components';
import {
  Ban,
  CircleDollarSign,
  ClipboardCheck,
  Eye,
  Pencil,
  Loader2,
  ChevronsUpDown,
  Check,
  ExternalLink,
  History,
  Info,
  Send,
  XCircle,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import type {
  NoaResponse,
  NoaListItem,
  NoaStatusCounts,
  NoaListSortBy,
} from '../services/api/types.gen';
import type { NOAManagerFilters } from '../store/slices/noaSlice';
import {
  SortableTableHead,
  type ColumnSort,
} from '../components/SortableTableHead';
import {
  listNoasNoasGet,
  getNoaNoasNoaIdGet,
  exportNoasNoasExportGet,
  getNoaStatusCountsNoasStatusCountsGet,
  listNoaPayersNoasPayersGet,
  submitNoa837iNoasNoaIdSubmitPost,
  markPaidNoasNoaIdMarkPaidPost,
  markNotApplicableNoasNoaIdMarkNotApplicablePost,
  markSentNoasNoaIdMarkSentPost,
  markRejectedNoasNoaIdMarkRejectedPost,
} from '../services/api/sdk.gen';
import NOAForm from '../components/NOAForm';
import { EntityWorkLogDialog } from '../components/worklog/EntityWorkLogDialog';
import {
  NOA_WORK_LOG_DIALOG_PROPS,
  loadNoaWorkLog,
} from '../components/worklog/noaWorkLog';
import {
  ExportMenuButton,
  UserFacingExportError,
  type ExportFormat,
} from '../components/common/ExportMenuButton';
import { formatTabCount } from '../components/billing/formatTabCount';
import { PayerReasonBadge } from '../components/billing/claimReason/PayerReasonBadge';
import { getNoaStatusTone } from '../components/billing/noaStatusTone';
import {
  NoRemittancesBadge,
  NoRemittancesNotice,
} from '../components/billing/NoRemittances';
import { buildNoaExportParams } from '../components/billing/billingExportParams';
import { TestPatientSubmitConfirmDialog } from '../components/TestPatientSubmitConfirmDialog';
import SelectionActionBar from '../components/SelectionActionBar';
import type { SelectionAction } from '../components/SelectionActionBar';
import {
  isTestPatientAckRequired,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../utils/downloadFile';
import { formatDateToMMDDYYYY, parseISOToLocalDate } from '../utils/dateUtils';
import { ReportLink } from '../components/reporting/ReportLink';
import { patientChartPath } from '../utils/navigation';
import { getCommonPayerName, shortenPayerName } from '../utils/noaPayer';
import { useToast } from '../hooks/useToastContext';
import { PermissionSlug } from '../constants/featureFlags';
import { ClinicSelect } from '../components/clinic/ClinicSelect';
import { ExcludePreTakeoverFilterCheckbox } from '../components/filters/ExcludePreTakeoverFilterCheckbox';

const STATUS_BY_TAB: Record<string, string> = {
  not_yet_ready: 'not_ready',
  unsent: 'ready_to_send',
  rejected: 'rejected',
  sent: 'sent,acknowledged,paid,accepted,not_applicable',
};

const DAYS_LEFT_LABELS: Record<string, string> = {
  all: 'All',
  overdue: 'Overdue',
  due_today: 'Due Today',
  due_tomorrow: 'Due Tomorrow',
  due_2_5_days: 'Due in 2-5 days',
};

type NoaSortColumn = NoaListSortBy;

type NoaSortState = ColumnSort<NoaSortColumn>;

function DaysLeftBadge({ daysLeft }: { daysLeft: number | null }) {
  if (daysLeft === null) {
    return <span className='text-muted-foreground'>—</span>;
  }
  if (daysLeft < 0) {
    return (
      <Badge className='whitespace-nowrap bg-red-100 text-red-700 border-red-200'>
        {Math.abs(daysLeft)}d overdue
      </Badge>
    );
  }
  if (daysLeft === 0) {
    return (
      <Badge className='whitespace-nowrap bg-orange-100 text-orange-700 border-orange-200'>
        Due today
      </Badge>
    );
  }
  return (
    <Badge className='whitespace-nowrap bg-orange-50 text-orange-600 border-orange-200'>
      {daysLeft}d left
    </Badge>
  );
}

function StatusBadge({
  status,
  noaId,
  reasonCount,
}: {
  status: string;
  noaId: string;
  reasonCount?: number;
}) {
  const labels: Record<string, string> = {
    not_ready: 'Not Ready',
    ready_to_send: 'Ready to Send',
    sent: 'Sent',
    acknowledged: 'Acknowledged',
    paid: 'Paid',
    accepted: 'Accepted',
    rejected: 'Rejected',
    not_applicable: 'N/A',
  };
  // PayerReasonBadge renders a plain Badge when there is nothing to show, so
  // every other status keeps today's appearance and only a refused notice gains
  // the hover panel. A rejected notice earns one even with no
  // reasons, because the drawer behind it holds the last-checked time.
  return (
    <PayerReasonBadge
      kind='noa'
      id={noaId}
      label={labels[status] ?? status}
      tone={getNoaStatusTone(status)}
      className='whitespace-nowrap'
      reasonCount={reasonCount}
      status={status}
      isRefusal={status === 'rejected'}
    />
  );
}

// countKey ties each tab to its field in the /noas/status-counts response
// (filtered like the list endpoints).
const NOA_TABS: {
  value: string;
  label: string;
  countKey: keyof NoaStatusCounts;
}[] = [
  { value: 'not_yet_ready', label: 'Not Yet Ready', countKey: 'not_yet_ready' },
  { value: 'unsent', label: 'Unsent', countKey: 'unsent' },
  { value: 'rejected', label: 'Rejected', countKey: 'rejected' },
  { value: 'sent', label: 'Sent', countKey: 'sent' },
];

function PayerCombobox({
  payers,
  value,
  onChange,
}: {
  payers: { id: string; name: string; noa_count: number }[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const selected = payers.find(p => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* h-8 + truncate: long payer names must not wrap and grow the
            filter row (matches Search / selectSize default). Full name stays
            in title. */}
        <button
          type='button'
          title={selected ? selected.name : 'All payers'}
          className='flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm text-left hover:bg-accent transition-colors'
        >
          <span
            className={`min-w-0 truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            {selected ? selected.name : 'All payers'}
          </span>
          <ChevronsUpDown className='h-3 w-3 shrink-0 opacity-50' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-0' align='start'>
        <Command>
          <CommandInput placeholder='Search payers…' className='text-xs' />
          <CommandList>
            <CommandEmpty className='text-xs py-2 text-center text-muted-foreground'>
              No payers found.
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value='all'
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={`mr-2 h-3 w-3 shrink-0 ${value === null ? 'opacity-100' : 'opacity-0'}`}
                />
                <span className='text-xs'>All payers</span>
              </CommandItem>
              {payers.map(p => {
                const hasNoas = p.noa_count > 0;
                return (
                  <CommandItem
                    key={p.id}
                    value={p.name}
                    disabled={!hasNoas}
                    onSelect={() => {
                      if (hasNoas) {
                        onChange(p.id);
                        setOpen(false);
                      }
                    }}
                    className={!hasNoas ? 'opacity-40 cursor-not-allowed' : ''}
                  >
                    <Check
                      className={`mr-2 h-3 w-3 shrink-0 ${value === p.id ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <span className='text-xs flex-1'>{p.name}</span>
                    <span className='text-xs text-muted-foreground ml-1'>
                      ({p.noa_count})
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type BulkStatusAction = 'mark-paid' | 'mark-sent' | 'mark-not-applicable';

const BULK_STATUS_COPY: Record<
  BulkStatusAction,
  { label: string; past: string }
> = {
  'mark-paid': { label: 'Mark Paid', past: 'marked paid' },
  'mark-sent': { label: 'Mark Sent', past: 'marked sent' },
  'mark-not-applicable': { label: 'Mark N/A', past: 'marked N/A' },
};

const noaCountLabel = (count: number) =>
  `${count} NOA${count === 1 ? '' : 's'}`;

const NOAManager: React.FC = () => {
  const {
    noaManagerFilters,
    setNOAManagerFilters,
    agencyClinics,
    fetchAgencyClinics,
    hasPermission,
  } = useStore(
    useShallow(state => ({
      noaManagerFilters: state.noaManagerFilters,
      setNOAManagerFilters: state.setNOAManagerFilters,
      agencyClinics: state.agencyClinics,
      fetchAgencyClinics: state.fetchAgencyClinics,
      hasPermission: state.hasPermission,
    }))
  );
  const isInternalAdmin = hasPermission(PermissionSlug.EMR_MANAGE);
  const canExportNoas = hasPermission(PermissionSlug.BILLING_EXPORT_NOAS);
  const canViewWorkLog = hasPermission(PermissionSlug.WORKLOG_VIEW);
  const showInactive = noaManagerFilters.showInactive ?? false;
  const hideTestPatients = noaManagerFilters.hideTestPatients ?? false;
  const hideImportedPatients = noaManagerFilters.hideImportedPatients ?? true;
  const excludePreTakeover = noaManagerFilters.excludePreTakeover !== false;

  const [noas, setNoas] = useState<NoaListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Sort is kept per tab so a Sent Date sort on the Sent tab doesn't leak
  // into tabs that don't show that column.
  const [sortsByTab, setSortsByTab] = useState<
    Partial<Record<NOAManagerFilters['activeTab'], NoaSortState>>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingNoa, setEditingNoa] = useState<NoaResponse | null>(null);
  // The row's history button opens the same log as the popup's.
  const [workLogNoaId, setWorkLogNoaId] = useState<string | null>(null);
  const loadWorkLog = useCallback(
    () => loadNoaWorkLog(workLogNoaId ?? ''),
    [workLogNoaId]
  );
  const [isBulkActing, setIsBulkActing] = useState(false);
  const [bulkRejectConfirmOpen, setBulkRejectConfirmOpen] = useState(false);
  const [testPatientAckOpen, setTestPatientAckOpen] = useState(false);
  const [pendingTestPatientIds, setPendingTestPatientIds] = useState<string[]>(
    []
  );
  const toast = useToast();

  const [payers, setPayers] = useState<
    { id: string; name: string; noa_count: number }[]
  >([]);
  const [tabCounts, setTabCounts] = useState<NoaStatusCounts | null>(null);
  // Tracks the latest fetchTabCounts request so a slow response from an
  // earlier filter state can't overwrite newer counts.
  const tabCountsGenerationRef = useRef(0);
  // Only the latest list request may update shared rows, totals, or loading.
  const noasGenerationRef = useRef(0);

  // Per-tab totals for the tab badges. The endpoint applies the same sidebar
  // filters (search, branch, payer) as the list endpoints, so each badge
  // shows how many NOAs in that tab match the current filters.
  const { patientSearch, branchId, payerId, daysLeftFilter, errorsFilter } =
    noaManagerFilters;
  const fetchTabCounts = useCallback(async () => {
    const generation = ++tabCountsGenerationRef.current;
    const resp = await getNoaStatusCountsNoasStatusCountsGet({
      query: {
        patient_search: patientSearch || undefined,
        branch_id: branchId || undefined,
        payer_id: payerId || undefined,
        show_inactive: showInactive,
        hide_test: hideTestPatients,
        hide_imported_patients: hideImportedPatients,
        exclude_pre_takeover: excludePreTakeover,
        days_left: daysLeftFilter,
        errors: errorsFilter,
      },
    });
    // Ignore stale responses when mutation-triggered refetches overlap.
    if (generation !== tabCountsGenerationRef.current) return;
    if (resp.error || !resp.data) {
      // Clear rather than keep: a stale count next to fresh data is worse
      // than showing no count at all.
      console.error('Failed to load NOA tab counts:', resp.error);
      setTabCounts(null);
      return;
    }
    setTabCounts(resp.data);
  }, [
    patientSearch,
    branchId,
    payerId,
    daysLeftFilter,
    errorsFilter,
    showInactive,
    hideTestPatients,
    hideImportedPatients,
    excludePreTakeover,
  ]);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);

  const fetchPayers = useCallback(async () => {
    const statusParam = STATUS_BY_TAB[noaManagerFilters.activeTab];
    const resp = await listNoaPayersNoasPayersGet({
      query: {
        status: statusParam || undefined,
        branch_id: noaManagerFilters.branchId || undefined,
        patient_search: noaManagerFilters.patientSearch || undefined,
        show_inactive: showInactive,
        hide_test: hideTestPatients,
        hide_imported_patients: hideImportedPatients,
        exclude_pre_takeover: excludePreTakeover,
      },
    });
    if (!resp.error && resp.data) {
      setPayers(resp.data as { id: string; name: string; noa_count: number }[]);
    }
  }, [
    noaManagerFilters.activeTab,
    noaManagerFilters.branchId,
    noaManagerFilters.patientSearch,
    showInactive,
    hideTestPatients,
    hideImportedPatients,
    excludePreTakeover,
  ]);

  useEffect(() => {
    fetchPayers();
  }, [fetchPayers]);

  const [searchInput, setSearchInput] = useState(
    noaManagerFilters.patientSearch
  );
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, []);

  const activeTab = noaManagerFilters.activeTab;
  const activeSort = sortsByTab[activeTab];

  // Three-state cycle (asc → desc → clear) so the default most-urgent-first
  // ordering stays reachable after sorting by another column.
  const handleSort = (column: NoaSortColumn) => {
    setSortsByTab(prev => {
      const current = prev[activeTab];
      if (current?.column !== column) {
        return { ...prev, [activeTab]: { column, direction: 'asc' as const } };
      }
      if (current.direction === 'asc') {
        return { ...prev, [activeTab]: { column, direction: 'desc' as const } };
      }
      return { ...prev, [activeTab]: undefined };
    });
    setPage(1);
  };

  useEffect(() => {
    fetchAgencyClinics();
  }, [fetchAgencyClinics]);

  const fetchNoas = useCallback(async () => {
    const generation = ++noasGenerationRef.current;
    setIsLoading(true);
    setSelectedIds(new Set());
    try {
      const statusParam =
        noaManagerFilters.statusFilter !== 'all'
          ? noaManagerFilters.statusFilter
          : STATUS_BY_TAB[activeTab];
      const sort = sortsByTab[activeTab];
      const resp = await listNoasNoasGet({
        query: {
          status: statusParam,
          page,
          page_size: 25,
          patient_search: noaManagerFilters.patientSearch || undefined,
          branch_id: noaManagerFilters.branchId || undefined,
          payer_id: noaManagerFilters.payerId || undefined,
          show_inactive: showInactive,
          hide_test: hideTestPatients,
          hide_imported_patients: hideImportedPatients,
          exclude_pre_takeover: excludePreTakeover,
          days_left: noaManagerFilters.daysLeftFilter,
          errors: noaManagerFilters.errorsFilter,
          sort_by: (sort?.column as NoaListSortBy) || undefined,
          sort_direction: sort?.direction,
        },
      });
      if (
        generation === noasGenerationRef.current &&
        !resp.error &&
        resp.data
      ) {
        setNoas(resp.data.items);
        setTotal(resp.data.total);
      }
    } finally {
      if (generation === noasGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    activeTab,
    page,
    noaManagerFilters,
    showInactive,
    hideTestPatients,
    hideImportedPatients,
    excludePreTakeover,
    sortsByTab,
  ]);

  // Every mutation must refresh both: the list (rows move between tabs) and
  // the tab counts. Filter changes refetch each via its own effect.
  const refreshNoasAndCounts = useCallback(async () => {
    await Promise.all([fetchNoas(), fetchTabCounts()]);
  }, [fetchNoas, fetchTabCounts]);

  const handleExportNoas = useCallback(
    async (format: ExportFormat) => {
      try {
        const response = await exportNoasNoasExportGet({
          query: buildNoaExportParams(
            {
              patient_search: noaManagerFilters.patientSearch || undefined,
              branch_id: noaManagerFilters.branchId || undefined,
              payer_id: noaManagerFilters.payerId || undefined,
              show_inactive: showInactive,
              hide_test: hideTestPatients,
              hide_imported_patients: hideImportedPatients,
              exclude_pre_takeover: excludePreTakeover,
              days_left: noaManagerFilters.daysLeftFilter,
              errors: noaManagerFilters.errorsFilter,
              // Forward the active tab's sort so the file comes back in the
              // order the screen was sorted by, not the SOC-date default.
              sort_by: sortsByTab[activeTab]?.column,
              sort_direction: sortsByTab[activeTab]?.direction,
            },
            format
          ),
          responseType: 'blob' as const,
        });
        if (response.error || !response.data) {
          throw new UserFacingExportError(
            await getUserFacingApiMessageAsync(
              {
                response: {
                  status: response.response?.status,
                  data: response.response?.data ?? response.error,
                },
              },
              'Unable to export NOAs. Please try again.'
            )
          );
        }
        downloadResponseAsFile(response as never, `noas.${format}`);
      } catch (error) {
        console.error('Failed to export NOAs', error);
        if (error instanceof UserFacingExportError) throw error;
        throw new UserFacingExportError(
          await getUserFacingApiMessageAsync(
            error,
            'Unable to export NOAs. Please try again.'
          )
        );
      }
    },
    [
      noaManagerFilters,
      showInactive,
      hideTestPatients,
      hideImportedPatients,
      excludePreTakeover,
      sortsByTab,
      activeTab,
    ]
  );

  useEffect(() => {
    fetchNoas();
  }, [fetchNoas]);

  const handlePatientSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setNOAManagerFilters({ patientSearch: value });
      setPage(1);
      searchTimerRef.current = null;
    }, 300);
  };

  const handleTabChange = (tab: string) => {
    setNOAManagerFilters({
      activeTab: tab as NOAManagerFilters['activeTab'],
      statusFilter: 'all',
    });
    setPage(1);
    setSelectedIds(new Set());
  };

  const openEdit = async (id: string) => {
    const resp = await getNoaNoasNoaIdGet({
      path: { noa_id: id },
    });
    if (!resp.error && resp.data) {
      setEditingNoa(resp.data);
    }
  };

  const handleSaved = (updated: NoaResponse) => {
    setNoas(prev =>
      prev.map(n =>
        n.id === updated.id
          ? {
              ...n,
              status: updated.status,
              days_left: updated.days_left,
              is_late: updated.is_late,
              validation_errors: updated.validation_errors,
              editable: updated.editable,
            }
          : n
      )
    );
    if (editingNoa?.id === updated.id) {
      setEditingNoa(updated);
    }
    // Refresh so status changes (e.g. sent) move rows to the correct tab
    refreshNoasAndCounts();
  };

  // A NOA with outstanding validation errors can't be submitted — the backend
  // rejects it with a 409 — so keep it out of bulk selection even though it's
  // still sitting in the unsent tab.
  const isSubmittable = (n: NoaListItem) =>
    n.status === 'ready_to_send' && n.validation_errors.length === 0;

  // Selection helpers
  const selectableNoas =
    activeTab === 'sent' || activeTab === 'rejected'
      ? noas
      : activeTab === 'unsent'
        ? noas.filter(isSubmittable)
        : [];
  const selectableNoaIds = selectableNoas.map(n => n.id);
  const selectedNoas = noas.filter(noa => selectedIds.has(noa.id));
  const allSelectableSelected =
    selectableNoaIds.length > 0 &&
    selectableNoaIds.every(id => selectedIds.has(id));

  const getSubmitActionLabel = (items: NoaListItem[]) => {
    const payerName = getCommonPayerName(items);
    return payerName
      ? `Submit to ${shortenPayerName(payerName)}`
      : 'Submit to selected payors';
  };

  // The full payer name, when the button label had to shorten it.
  const getSubmitActionLabelTitle = (items: NoaListItem[]) => {
    const payerName = getCommonPayerName(items);
    return payerName && shortenPayerName(payerName) !== payerName
      ? `Submit to ${payerName}`
      : '';
  };

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      const next = new Set(selectedIds);
      selectableNoaIds.forEach(id => next.delete(id));
      setSelectedIds(next);
    } else {
      setSelectedIds(new Set(selectableNoaIds));
    }
  };

  const toggleSelect = (id: string, isReady: boolean) => {
    if (!isReady) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const bulkSubmitNoas = (ids: string[], acknowledge: boolean) =>
    Promise.allSettled(
      ids.map(id =>
        submitNoa837iNoasNoaIdSubmitPost({
          path: { noa_id: id },
          query: acknowledge ? { acknowledge_test_patient: true } : undefined,
        })
      )
    );

  const payerText = (payerName: string | null) => payerName ?? 'the payer';

  const reportBulkResults = (
    count: number,
    results: PromiseSettledResult<{ error?: unknown }>[],
    payerName: string | null
  ) => {
    const failed = results.filter(
      r =>
        r.status === 'rejected' || (r.status === 'fulfilled' && r.value.error)
    );
    const succeeded = count - failed.length;
    if (failed.length === 0) {
      toast.success(
        `Successfully submitted ${succeeded} NOA${succeeded !== 1 ? 's' : ''} to ${payerText(payerName)}.`
      );
    } else {
      toast.error(
        `${succeeded} submitted, ${failed.length} failed. Check NOA statuses for details.`
      );
    }
  };

  const handleBulkSubmit = async (items: NoaListItem[]) => {
    if (items.length === 0) return;
    setIsBulkActing(true);
    try {
      const ids = items.map(noa => noa.id);
      const results = await bulkSubmitNoas(ids, false);
      // Test patients come back as an ack-required 409 — not a failure, a confirm.
      const ackRequiredIds = ids.filter(
        (_, i) =>
          results[i].status === 'fulfilled' &&
          isTestPatientAckRequired(
            (results[i] as PromiseFulfilledResult<{ error?: unknown }>).value
          )
      );
      const settled = ids.filter(id => !ackRequiredIds.includes(id));
      if (settled.length > 0) {
        reportBulkResults(
          settled.length,
          results.filter((_, i) => !ackRequiredIds.includes(ids[i])),
          getCommonPayerName(items.filter(noa => settled.includes(noa.id)))
        );
      }
      await refreshNoasAndCounts();
      if (ackRequiredIds.length > 0) {
        setPendingTestPatientIds(ackRequiredIds);
        setTestPatientAckOpen(true);
      }
    } finally {
      setIsBulkActing(false);
    }
  };

  const confirmTestPatientBulkSubmit = async () => {
    const ackIds = pendingTestPatientIds;
    setTestPatientAckOpen(false);
    setPendingTestPatientIds([]);
    if (ackIds.length === 0) return;
    setIsBulkActing(true);
    try {
      const results = await bulkSubmitNoas(ackIds, true);
      reportBulkResults(
        ackIds.length,
        results,
        getCommonPayerName(noas.filter(n => ackIds.includes(n.id)))
      );
      await refreshNoasAndCounts();
    } finally {
      setIsBulkActing(false);
    }
  };

  const handleBulkStatusChange = async (
    action: BulkStatusAction,
    items: NoaListItem[]
  ) => {
    if (items.length === 0) return;
    setIsBulkActing(true);
    try {
      const sdkFn =
        action === 'mark-paid'
          ? (id: string) =>
              markPaidNoasNoaIdMarkPaidPost({ path: { noa_id: id } })
          : action === 'mark-sent'
            ? (id: string) =>
                markSentNoasNoaIdMarkSentPost({ path: { noa_id: id } })
            : (id: string) =>
                markNotApplicableNoasNoaIdMarkNotApplicablePost({
                  path: { noa_id: id },
                });
      const results = await Promise.allSettled(items.map(noa => sdkFn(noa.id)));
      const failures = results.flatMap(r =>
        r.status === 'rejected'
          ? [r.reason]
          : r.value.error
            ? [r.value.error]
            : []
      );
      if (failures.length > 0) {
        console.error(
          `Bulk ${action} failed for ${failures.length} NOAs`,
          failures
        );
        toast.error(
          `${items.length - failures.length} ${BULK_STATUS_COPY[action].past}, ${failures.length} failed. Refresh and try again.`
        );
      }
      await refreshNoasAndCounts();
    } finally {
      setIsBulkActing(false);
    }
  };

  // Mirrors the single-NOA gate: out with the payer, and not parked as filed
  // in the External EMR, which Roger never reopens.
  const isMarkableRejected = (n: NoaListItem) =>
    ['sent', 'acknowledged', 'accepted'].includes(n.status) &&
    !n.accepted_externally;

  // Name the payer of the NOAs the action will actually touch; fall back to
  // the whole selection when none qualify.
  const eligibleRejectNoas = selectedNoas.filter(isMarkableRejected);
  const rejectPayerName = getCommonPayerName(
    eligibleRejectNoas.length > 0 ? eligibleRejectNoas : selectedNoas
  );
  const rejectPayerText = payerText(rejectPayerName);

  const handleBulkMarkRejected = async () => {
    setBulkRejectConfirmOpen(false);
    const ids = selectedNoas.filter(isMarkableRejected).map(n => n.id);
    if (ids.length === 0) {
      toast.error(
        'None of the selected NOAs can be marked as rejected. Only NOAs sent to the payer and not filed in the External EMR qualify.'
      );
      return;
    }
    setIsBulkActing(true);
    try {
      const results = await Promise.allSettled(
        ids.map(id =>
          markRejectedNoasNoaIdMarkRejectedPost({ path: { noa_id: id } })
        )
      );
      const failed = results.filter(
        r =>
          r.status === 'rejected' ||
          (r.status === 'fulfilled' && (r.value as { error?: unknown }).error)
      ).length;
      const skipped = selectedNoas.length - ids.length;
      const marked = ids.length - failed;
      const detail = [
        failed > 0 ? `${failed} failed` : null,
        skipped > 0 ? `${skipped} not eligible` : null,
      ].filter(Boolean);
      if (detail.length === 0) {
        toast.success(
          `Marked ${marked} NOA${marked !== 1 ? 's' : ''} as rejected.`
        );
      } else {
        toast.error(
          `${marked} marked as rejected, ${detail.join(', ')}. Check NOA statuses for details.`
        );
      }
      await refreshNoasAndCounts();
    } finally {
      setIsBulkActing(false);
    }
  };

  const bulkActionDisabledReason = () =>
    isBulkActing ? 'Action in progress' : undefined;

  // Status writes sit next to each other in the dropdown, so each one confirms
  // the count and the status before it fires, like the Billing Manager bar.
  const statusAction = (
    action: BulkStatusAction,
    icon: SelectionAction<NoaListItem>['icon'],
    destructive = false
  ): SelectionAction<NoaListItem> => ({
    key: action,
    label: BULK_STATUS_COPY[action].label,
    icon,
    destructive,
    disabledReason: bulkActionDisabledReason,
    confirm: items => ({
      title: `${BULK_STATUS_COPY[action].label}?`,
      body: `${noaCountLabel(items.length)} will be ${BULK_STATUS_COPY[action].past}.`,
      choices: [
        { key: 'cancel', label: 'Cancel', variant: 'outline' },
        {
          key: 'confirm',
          label: BULK_STATUS_COPY[action].label,
          variant: destructive ? 'destructive' : 'default',
        },
      ],
    }),
    run: items => handleBulkStatusChange(action, items),
  });

  // The selection surface is per tab: the unsent tab submits, the sent tab
  // re-states status, and both can retire a NOA as N/A.
  const selectionActions: SelectionAction<NoaListItem>[] = [
    ...(activeTab === 'unsent'
      ? [
          {
            key: 'submit',
            label: getSubmitActionLabel,
            labelTitle: getSubmitActionLabelTitle,
            icon: Send,
            disabledReason: bulkActionDisabledReason,
            run: handleBulkSubmit,
          } satisfies SelectionAction<NoaListItem>,
        ]
      : []),
    ...(activeTab === 'sent'
      ? [
          statusAction('mark-paid', CircleDollarSign),
          statusAction('mark-sent', ClipboardCheck),
          {
            key: 'mark-rejected',
            label: 'Mark Rejected',
            icon: XCircle,
            destructive: true,
            disabledReason: bulkActionDisabledReason,
            run: () => setBulkRejectConfirmOpen(true),
          } satisfies SelectionAction<NoaListItem>,
        ]
      : []),
    statusAction('mark-not-applicable', Ban, true),
  ];

  const removeFromSelection = (noa: NoaListItem) => {
    const next = new Set(selectedIds);
    next.delete(noa.id);
    setSelectedIds(next);
  };

  const renderTable = (
    items: NoaListItem[],
    showDaysLeft: boolean,
    sortableStatus = false
  ) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className='w-8'>
            {activeTab !== 'not_yet_ready' && (
              <Checkbox
                checked={allSelectableSelected}
                onCheckedChange={toggleSelectAll}
                checkboxSize='sm'
              />
            )}
          </TableHead>
          {showDaysLeft && (
            <SortableTableHead
              column='days_left'
              label='Days Left'
              activeSort={activeSort}
              onSort={handleSort}
            />
          )}
          {/* Every tab but Sent holds a single status, so sorting by Status
              is only meaningful on the Sent tab. */}
          {!sortableStatus ? (
            <TableHead>Status</TableHead>
          ) : (
            <SortableTableHead
              column='status'
              label='Status'
              activeSort={activeSort}
              onSort={handleSort}
            />
          )}
          {/* The Sent tab had a "Why" column beside this one. Its reasons moved
              onto the status badge, so the column is gone.*/}
          {/* Errors and Subscriber ID are computed per page in Python, so
              they can't be server-sorted and stay plain headers. */}
          {showDaysLeft && <TableHead>Errors</TableHead>}
          <TableHead>Subscriber ID</TableHead>
          <SortableTableHead
            column='patient'
            label='Patient'
            activeSort={activeSort}
            onSort={handleSort}
          />
          <SortableTableHead
            column='soc_date'
            label='Start of Care'
            activeSort={activeSort}
            onSort={handleSort}
          />
          {!showDaysLeft && (
            <SortableTableHead
              column='sent_date'
              label='Sent Date'
              activeSort={activeSort}
              onSort={handleSort}
            />
          )}
          {/* Blank for a notice nobody sent from Roger (imported, accepted
              through the external EMR). */}
          {!showDaysLeft && <TableHead>Sent By</TableHead>}
          <SortableTableHead
            column='branch'
            label='Branch'
            activeSort={activeSort}
            onSort={handleSort}
          />
          <SortableTableHead
            column='payer'
            label='Payer'
            activeSort={activeSort}
            onSort={handleSort}
          />
          <TableHead className='w-8'></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.length === 0 && !isLoading && (
          <TableRow>
            <TableCell
              colSpan={10}
              className='text-center text-muted-foreground py-8'
            >
              No NOAs found.
            </TableCell>
          </TableRow>
        )}
        {items.map(noa => {
          const canSubmit = isSubmittable(noa);
          const isSelected = selectedIds.has(noa.id);
          const socFormatted = noa.soc_date
            ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.soc_date))
            : '—';
          return (
            <TableRow key={noa.id} className={isSelected ? 'bg-accent/20' : ''}>
              <TableCell>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() =>
                    toggleSelect(
                      noa.id,
                      canSubmit ||
                        activeTab === 'sent' ||
                        activeTab === 'rejected'
                    )
                  }
                  disabled={
                    activeTab === 'not_yet_ready' ||
                    (activeTab === 'unsent' && !canSubmit)
                  }
                  checkboxSize='sm'
                />
              </TableCell>
              {showDaysLeft && (
                <TableCell>
                  <DaysLeftBadge daysLeft={noa.days_left} />
                </TableCell>
              )}
              <TableCell>
                <span className='inline-flex flex-wrap items-center gap-1'>
                  <StatusBadge
                    status={noa.status}
                    noaId={noa.id}
                    reasonCount={noa.reason_count}
                  />
                  {noa.accepted_externally && (
                    <Badge
                      variant='outline'
                      className='whitespace-nowrap text-xs'
                      title='Filed in External EMR. Roger did not send this notice and will not send another.'
                      data-testid='noa-accepted-externally'
                    >
                      External EMR
                    </Badge>
                  )}
                  {noa.receives_remittances === false && <NoRemittancesBadge />}
                </span>
                {/* Why the notice exists, so a biller who did not
                    create it can tell where it came from. A second line under
                    the status rather than a column: w-0 + min-w-full lets it
                    wrap inside the cell's existing width, so it adds none. */}
                <div
                  className='mt-1 w-0 min-w-full whitespace-normal break-words text-xs leading-tight text-muted-foreground'
                  data-testid='noa-creation-reason'
                >
                  {noa.creation_reason_label}
                </div>
              </TableCell>
              {showDaysLeft && (
                <TableCell>
                  {noa.validation_errors.length > 0 ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className='text-xs text-red-600 cursor-default underline decoration-dotted'>
                            {noa.validation_errors.length} error
                            {noa.validation_errors.length !== 1 ? 's' : ''}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className='max-w-xs'>
                          <ul className='space-y-0.5'>
                            {noa.validation_errors.map((e, i) => (
                              <li key={i} className='text-xs'>
                                • {e.message}
                              </li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className='text-xs text-green-600'>OK</span>
                  )}
                </TableCell>
              )}
              <TableCell className='text-sm'>
                {noa.subscriber_id || '—'}
              </TableCell>
              <TableCell className='text-sm font-medium'>
                <ReportLink to={patientChartPath(noa.patient_id)}>
                  {noa.patient_last_name}, {noa.patient_first_name}
                </ReportLink>
              </TableCell>
              <TableCell className='text-sm'>{socFormatted}</TableCell>
              {!showDaysLeft && (
                <TableCell className='text-sm'>
                  {noa.sent_date
                    ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.sent_date))
                    : '—'}
                </TableCell>
              )}
              {!showDaysLeft && (
                <TableCell className='text-sm'>
                  {noa.sent_by_name || '—'}
                </TableCell>
              )}
              <TableCell className='text-sm'>
                {noa.branch_name || '—'}
              </TableCell>
              <TableCell className='text-sm'>{noa.payer_name || '—'}</TableCell>
              <TableCell>
                <div className='flex items-center gap-1'>
                  {/* A sent/accepted/paid NOA can only be viewed —
                      the eye opens the same popup in read-only mode. */}
                  <button
                    onClick={() => openEdit(noa.id)}
                    className='text-muted-foreground hover:text-primary'
                    aria-label={
                      noa.editable !== false ? 'Edit NOA' : 'View NOA'
                    }
                    title={
                      noa.editable !== false ? undefined : 'View (read-only)'
                    }
                  >
                    {noa.editable !== false ? (
                      <Pencil className='h-4 w-4' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </button>
                  {canViewWorkLog && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setWorkLogNoaId(noa.id)}
                            className='text-muted-foreground hover:text-primary'
                            aria-label='Work log'
                          >
                            <History className='h-4 w-4' />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Work log</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {!showDaysLeft && noa.stedi_portal_url && isInternalAdmin && (
                    <a
                      href={noa.stedi_portal_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-muted-foreground hover:text-primary'
                    >
                      <ExternalLink className='h-4 w-4' />
                    </a>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <div className='flex flex-col h-full'>
      <div className='flex-1 flex flex-col overflow-hidden'>
        {/* variant='purple' matches the other Billing Manager tab bars that
            carry TabsCount — the pill's active-state styling (white on
            white/20) assumes the purple active-tab background and disappears
            on the default variant's white one. */}
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          variant='purple'
          className='flex-1 flex flex-col overflow-hidden'
        >
          <div className='pb-3 border-b flex flex-wrap items-center justify-between gap-2'>
            {/* Badged triggers can overflow a ~390px viewport, so below `sm`
                the list grows and triggers shrink/wrap instead of clipping.
                Desktop keeps the standard single-row tab bar. */}
            <div className='flex min-w-0 items-center gap-2'>
              <TabsList className='max-sm:h-auto max-sm:flex-wrap'>
                {NOA_TABS.map(tab => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className='min-w-0 max-sm:whitespace-normal'
                  >
                    <span className='inline-flex min-w-0 flex-wrap items-center justify-center gap-x-1 gap-y-0.5 sm:flex-nowrap'>
                      <span>{tab.label}</span>
                      <TabsCount
                        label={tab.label}
                        value={formatTabCount(tabCounts?.[tab.countKey])}
                      />
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {/* Beside the tabs, so loading never adds a row above them. */}
              {isLoading && (
                <Loader2
                  aria-label='Loading'
                  className='h-4 w-4 shrink-0 animate-spin text-muted-foreground'
                />
              )}
            </div>

            <div className='flex flex-wrap items-center gap-2'>
              {canExportNoas ? (
                <ExportMenuButton onExport={handleExportNoas} />
              ) : null}
            </div>
          </div>

          {/* Filters sit in a row above the table, like Billing Manager's
              toolbar; below `md` they fall back to a compact 2-col grid. */}
          <div className='border-b py-3 grid grid-cols-2 gap-x-3 gap-y-3 md:flex md:flex-wrap md:items-end'>
            <FilterToolbarSearch
              value={searchInput}
              onChange={handlePatientSearchChange}
              label='Search'
              placeholder='Search patient...'
              containerClassName={`${filterToolbarSearchGrowClassName} col-span-2`}
              className='w-full'
            />

            {/* Clinic / branch filter */}
            <div className={filterToolbarClinicClassName}>
              {/* Own FormLabel so Branch matches Search siblings; ClinicSelect's
                  built-in label is text-xs via VirtualizedFormSelect. */}
              <FormLabel htmlFor='noa-branch-filter' showTooltip={false}>
                Branch
              </FormLabel>
              <ClinicSelect
                inputId='noa-branch-filter'
                className='w-full'
                clinics={agencyClinics}
                includeAllOption
                allOptionValue='all'
                allOptionLabel='All branches'
                value={noaManagerFilters.branchId ?? 'all'}
                onChange={e => {
                  const v = e.target.value;
                  setNOAManagerFilters({ branchId: v === 'all' ? null : v });
                  setPage(1);
                }}
                placeholder='All branches'
              />
            </div>

            {/* Payer filter */}
            <div className={filterToolbarControlLgClassName}>
              <FormLabel showTooltip={false}>Payer</FormLabel>
              <PayerCombobox
                payers={payers}
                value={noaManagerFilters.payerId}
                onChange={v => {
                  setNOAManagerFilters({ payerId: v });
                  setPage(1);
                }}
              />
            </div>

            {/* Days left filter */}
            <div className={filterToolbarControlSmClassName}>
              <FormLabel htmlFor='noa-days-left-filter' showTooltip={false}>
                Days Left
              </FormLabel>
              <Select
                value={noaManagerFilters.daysLeftFilter}
                onValueChange={v => {
                  setNOAManagerFilters({
                    daysLeftFilter: v as NOAManagerFilters['daysLeftFilter'],
                  });
                  setPage(1);
                }}
              >
                <SelectTrigger
                  id='noa-days-left-filter'
                  selectSize='default'
                  className='w-full'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DAYS_LEFT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status filter — only meaningful on Sent tab */}
            {activeTab === 'sent' && (
              <div className={filterToolbarControlSmClassName}>
                <FormLabel htmlFor='noa-status-filter' showTooltip={false}>
                  Status
                </FormLabel>
                <Select
                  value={noaManagerFilters.statusFilter}
                  onValueChange={v => {
                    setNOAManagerFilters({ statusFilter: v });
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    id='noa-status-filter'
                    selectSize='default'
                    className='w-full'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All</SelectItem>
                    <SelectItem value='sent'>Sent</SelectItem>
                    <SelectItem value='acknowledged'>Acknowledged</SelectItem>
                    <SelectItem value='accepted'>Accepted</SelectItem>
                    <SelectItem value='paid'>Paid</SelectItem>
                    <SelectItem value='not_applicable'>N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Errors filter */}
            {(activeTab === 'not_yet_ready' || activeTab === 'unsent') && (
              <div className={filterToolbarControlSmClassName}>
                <FormLabel htmlFor='noa-errors-filter' showTooltip={false}>
                  Errors
                </FormLabel>
                <Select
                  value={noaManagerFilters.errorsFilter}
                  onValueChange={v => {
                    setNOAManagerFilters({
                      errorsFilter: v as NOAManagerFilters['errorsFilter'],
                    });
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    id='noa-errors-filter'
                    selectSize='default'
                    className='w-full'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All</SelectItem>
                    <SelectItem value='has_errors'>Has Errors</SelectItem>
                    <SelectItem value='no_errors'>No Errors</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* h-8 matches Search / default selects under items-end. */}
            <div className='flex h-8 items-center gap-2 self-end'>
              <Checkbox
                id='noa-show-inactive'
                checked={showInactive}
                onCheckedChange={(checked: boolean) => {
                  setNOAManagerFilters({ showInactive: !!checked });
                  setPage(1);
                }}
                checkboxSize='sm'
              />
              <label
                htmlFor='noa-show-inactive'
                className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Show inactive patients
              </label>
            </div>

            <div className='flex h-8 items-center gap-2 self-end'>
              <Checkbox
                id='noa-hide-imported-patients'
                checked={hideImportedPatients}
                onCheckedChange={(checked: boolean) => {
                  setNOAManagerFilters({ hideImportedPatients: !!checked });
                  setPage(1);
                }}
                checkboxSize='sm'
              />
              <label
                htmlFor='noa-hide-imported-patients'
                className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Hide imported patients
              </label>
            </div>

            <ExcludePreTakeoverFilterCheckbox
              id='noa-exclude-pre-takeover'
              checked={excludePreTakeover}
              onCheckedChange={checked => {
                setNOAManagerFilters({ excludePreTakeover: checked });
                setPage(1);
              }}
            />

            <div className='flex h-8 items-center gap-2 self-end'>
              <Checkbox
                id='noa-hide-test-patients'
                checked={hideTestPatients}
                onCheckedChange={(checked: boolean) => {
                  setNOAManagerFilters({ hideTestPatients: !!checked });
                  setPage(1);
                }}
                checkboxSize='sm'
              />
              <label
                htmlFor='noa-hide-test-patients'
                className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Hide test patients
              </label>
            </div>
          </div>

          <div className='flex-1 overflow-auto'>
            <NoRemittancesNotice rows={noas} className='mt-3' />
            <TabsContent value='not_yet_ready' className='mt-0 h-full'>
              {renderTable(noas, true)}
            </TabsContent>
            <TabsContent value='unsent' className='mt-0 h-full'>
              {renderTable(noas, true)}
            </TabsContent>
            <TabsContent value='rejected' className='mt-0 h-full'>
              {renderTable(noas, false)}
            </TabsContent>
            <TabsContent value='sent' className='mt-0 h-full'>
              <div className='mt-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2'>
                <Info className='h-4 w-4 text-blue-600 shrink-0 mt-0.5' />
                <p className='text-sm text-blue-900'>
                  Many payers do not send an “Accepted” reply. If a NOA has been
                  Sent for a week or more with no rejection, it was most likely
                  accepted.
                </p>
              </div>
              {renderTable(noas, false, true)}
            </TabsContent>
          </div>
        </Tabs>

        {/* Pagination */}
        {total > 25 && (
          <div className='border-t pt-2 flex items-center justify-between'>
            <span className='text-sm text-muted-foreground'>
              Showing {(page - 1) * 25 + 1}–{Math.min(page * 25, total)} of{' '}
              {total}
            </span>
            <div className='flex gap-2'>
              <Button
                size='sm'
                variant='outline'
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                size='sm'
                variant='outline'
                onClick={() => setPage(p => p + 1)}
                disabled={page * 25 >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* NOA Form Sheet */}
      {editingNoa && (
        <NOAForm
          noa={editingNoa}
          open={!!editingNoa}
          onClose={() => setEditingNoa(null)}
          onSaved={handleSaved}
        />
      )}
      <EntityWorkLogDialog
        open={workLogNoaId !== null}
        onOpenChange={open => {
          if (!open) setWorkLogNoaId(null);
        }}
        {...NOA_WORK_LOG_DIALOG_PROPS}
        load={loadWorkLog}
      />
      <SelectionActionBar<NoaListItem>
        items={selectedNoas}
        onClear={() => setSelectedIds(new Set())}
        onRemoveItem={removeFromSelection}
        actions={selectionActions}
        getItemKey={noa => noa.id}
        getItemLabel={noa =>
          `${noa.patient_last_name}, ${noa.patient_first_name}`
        }
        getItemGroup={() => ({ key: 'noas', label: 'Selected NOAs' })}
      />
      <ConfirmationDialog
        open={bulkRejectConfirmOpen}
        title={`Are you sure ${rejectPayerText} rejected these NOAs?`}
        message={`Check again before continuing: only do this if a clearinghouse has confirmed that ${rejectPayerText} rejected every selected NOA. If one is still pending or was accepted, sending it again will be rejected automatically as a duplicate. NOAs filed in the External EMR and NOAs not sent to the payer are skipped. This cannot be undone from here.`}
        confirmLabel={`Yes, ${rejectPayerName ? shortenPayerName(rejectPayerName) : rejectPayerText} rejected them`}
        onAccept={() => void handleBulkMarkRejected()}
        onCancel={() => setBulkRejectConfirmOpen(false)}
      />
      <TestPatientSubmitConfirmDialog
        open={testPatientAckOpen}
        actionLabel='Notice of Admission'
        count={pendingTestPatientIds.length}
        loading={isBulkActing}
        onConfirm={() => void confirmTestPatientBulkSubmit()}
        onCancel={() => {
          setTestPatientAckOpen(false);
          setPendingTestPatientIds([]);
        }}
      />
    </div>
  );
};

export default NOAManager;
